import { useState } from 'react';

// The filter bar's calendar (FilterDateRange / FilterDate):
//
//   ‹   [September]  [2026]   ›
//   Mo Tu We Th Fr Sa Su
//   six weeks of days, the neighbouring months' days dimmed
//   ─────────────────────────
//               Today
//
// Clicking the month opens a 3 × 4 grid of months, clicking the year a
// 3 × 4 grid of years; picking one returns to the days. Colours are the
// calendar card's (.ftb-datepick-pop in FilterToolbar.css).

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const dayKey = (d) => d.getFullYear() * 10000 + d.getMonth() * 100 + d.getDate();
const sameDay = (a, b) => Boolean(a && b) && dayKey(a) === dayKey(b);

// The 42 days on show for a month: from the Monday on or before the 1st.
function monthDays(year, month) {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7;
  return Array.from({ length: 42 }, (_, i) => new Date(year, month, 1 - lead + i));
}

/**
 * Props:
 *  - value: Date | [Date, Date] | null
 *  - selectRange: pick a start day, then an end day
 *  - onChange: (Date) or, with selectRange, ([start, end | null])
 *  - onToday: what the Today link does
 *  - tileDisabled: ({ date, view }) => bool, for days
 *  - minDate: Date, the earliest day that can be picked
 *  - today: Date, the day ringed as today (default: the browser's)
 *  - openMonth: Date, the month to open on when nothing is picked
 */
export default function FilterCalendar({
  value,
  selectRange = false,
  onChange,
  onToday,
  tileDisabled,
  minDate,
  today = new Date(),
  openMonth
}) {
  const [start, end] = Array.isArray(value) ? value : [value, null];
  const shown = start || openMonth || today;

  const [view, setView] = useState('days');
  const [cursor, setCursor] = useState({ year: shown.getFullYear(), month: shown.getMonth() });
  const [yearFrom, setYearFrom] = useState(shown.getFullYear() - 4);
  const [hover, setHover] = useState(null);

  const awaitingEnd = selectRange && start && !end;

  const step = (dir) => {
    if (view === 'days') {
      const d = new Date(cursor.year, cursor.month + dir, 1);
      setCursor({ year: d.getFullYear(), month: d.getMonth() });
    } else if (view === 'months') {
      setCursor((c) => ({ ...c, year: c.year + dir }));
    } else {
      setYearFrom((y) => y + dir * 12);
    }
  };

  const isDisabled = (date) =>
    Boolean((minDate && dayKey(date) < dayKey(minDate)) || (tileDisabled && tileDisabled({ date, view: 'month' })));

  const pickDay = (date) => {
    if (!selectRange) return onChange(date);
    if (!awaitingEnd) return onChange([date, null]);
    setHover(null);
    return onChange(dayKey(date) < dayKey(start) ? [date, start] : [start, date]);
  };

  // The band between the two ends — or, while the end is being chosen, up to
  // the day under the pointer.
  const rangeEnd = end || (awaitingEnd ? hover : null);
  const [lo, hi] = rangeEnd && start ? [start, rangeEnd].sort((a, b) => a - b) : [null, null];

  return (
    <div className="ftb-cal">
      <div className="ftb-cal-head">
        <button type="button" className="ftb-cal-arrow" aria-label="Previous" onClick={() => step(-1)}>
          <i className="ph ph-caret-left" />
        </button>
        <div className="ftb-cal-title">
          <button
            type="button"
            className={`ftb-cal-pill${view === 'months' ? ' is-on' : ''}`}
            onClick={() => setView(view === 'months' ? 'days' : 'months')}
            aria-label="Choose month"
          >
            {new Date(cursor.year, cursor.month, 1).toLocaleDateString('en-GB', { month: 'long' })}
          </button>
          <button
            type="button"
            className={`ftb-cal-pill${view === 'years' ? ' is-on' : ''}`}
            onClick={() => {
              setYearFrom(cursor.year - 4);
              setView(view === 'years' ? 'days' : 'years');
            }}
            aria-label="Choose year"
          >
            {cursor.year}
          </button>
        </div>
        <button type="button" className="ftb-cal-arrow" aria-label="Next" onClick={() => step(1)}>
          <i className="ph ph-caret-right" />
        </button>
      </div>

      {view === 'days' && (
        <>
          <div className="ftb-cal-weekdays">
            {WEEKDAYS.map((w) => (
              <span key={w}>{w}</span>
            ))}
          </div>
          <div className="ftb-cal-days" onMouseLeave={() => setHover(null)}>
            {monthDays(cursor.year, cursor.month).map((date) => {
              const k = dayKey(date);
              const isEnd = sameDay(date, start) || sameDay(date, end);
              const inBand = lo && hi && k > dayKey(lo) && k < dayKey(hi);
              const cls = [
                'ftb-cal-day',
                date.getMonth() !== cursor.month && 'is-other',
                sameDay(date, today) && 'is-today',
                inBand && 'is-in-range',
                isEnd && 'is-picked'
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <button
                  key={k}
                  type="button"
                  className={cls}
                  disabled={isDisabled(date)}
                  onClick={() => pickDay(date)}
                  onMouseEnter={() => awaitingEnd && setHover(date)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
          <div className="ftb-cal-foot">
            <button type="button" className="ftb-cal-today" disabled={isDisabled(today)} onClick={onToday}>
              Today
            </button>
          </div>
        </>
      )}

      {view === 'months' && (
        <div className="ftb-cal-grid">
          {MONTHS_SHORT.map((m, i) => (
            <button
              key={m}
              type="button"
              className={`ftb-cal-cell${i === cursor.month ? ' is-picked' : ''}`}
              onClick={() => {
                setCursor((c) => ({ ...c, month: i }));
                setView('days');
              }}
            >
              {m}
            </button>
          ))}
        </div>
      )}

      {view === 'years' && (
        <div className="ftb-cal-grid">
          {Array.from({ length: 12 }, (_, i) => yearFrom + i).map((y) => (
            <button
              key={y}
              type="button"
              className={`ftb-cal-cell${y === cursor.year ? ' is-picked' : ''}${y === today.getFullYear() ? ' is-now' : ''}`}
              onClick={() => {
                setCursor((c) => ({ ...c, year: y }));
                setView('days');
              }}
            >
              {y}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
